import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { COUNTRIES, LEAD_SOURCES } from "../../lib/constants";
import { toast } from "sonner";

const EMPTY = {
  company_name: "", contact_name: "", email: "", phone: "", website: "",
  address: "", city: "", country: "", language: "",
  buyer_persona: "Other", lead_source: "", assigned_agent_id: "",
  lead_status: "New", priority: "B", potential_value: 0,
  product_interest: [], preferred_contact_channel: "Email",
  estimated_monthly_volume: 0, vat_number: "", company_size: "",
  notes: "", gdpr_consent: false,
};

export default function AddLeadModal({ open, onOpenChange, agents, onCreated }) {
  const [enums, setEnums] = useState({
    lead_statuses: [], buyer_personas: [], product_interests: [],
    priorities: [], contact_channels: [],
  });
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/meta/enums").then(({ data }) => setEnums(data));
  }, []);

  const toggleProduct = (p) => {
    setForm((f) => ({
      ...f,
      product_interest: f.product_interest.includes(p)
        ? f.product_interest.filter((x) => x !== p)
        : [...f.product_interest, p],
    }));
  };

  const submit = async () => {
    if (!form.company_name.trim()) return toast.error("Company name required");
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.assigned_agent_id) payload.assigned_agent_id = null;
      payload.potential_value = Number(payload.potential_value) || 0;
      payload.estimated_monthly_volume = Number(payload.estimated_monthly_volume) || 0;
      const { data } = await api.post("/leads", payload);
      toast.success("Lead created");
      onCreated?.(data);
      onOpenChange(false);
      setForm(EMPTY);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create lead");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="add-lead-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">New Lead</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Company Name *</Label>
            <Input data-testid="lead-company-input" value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
          </div>
          <div>
            <Label>Contact Name</Label>
            <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="lead-email-input" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <Label>Website</Label>
            <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
          </div>
          <div>
            <Label>City</Label>
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </div>
          <div>
            <Label>Country *</Label>
            <Select value={form.country || "__none"} onValueChange={(v) => setForm({ ...form, country: v === "__none" ? "" : v })}>
              <SelectTrigger data-testid="lead-country-select"><SelectValue placeholder="Select country" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— None —</SelectItem>
                {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Address</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div>
            <Label>Buyer Persona</Label>
            <Select value={form.buyer_persona} onValueChange={(v) => setForm({ ...form, buyer_persona: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {enums.buyer_personas.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Lead Source</Label>
            <Select value={form.lead_source || "__none"} onValueChange={(v) => setForm({ ...form, lead_source: v === "__none" ? "" : v })}>
              <SelectTrigger data-testid="lead-source-select"><SelectValue placeholder="Select source" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— None —</SelectItem>
                {LEAD_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.lead_status} onValueChange={(v) => setForm({ ...form, lead_status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {enums.lead_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {enums.priorities.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Monthly Volume (kg)</Label>
            <Input type="number" value={form.estimated_monthly_volume}
              onChange={(e) => setForm({ ...form, estimated_monthly_volume: e.target.value })} />
          </div>
          <div>
            <Label>Preferred Channel</Label>
            <Select value={form.preferred_contact_channel} onValueChange={(v) => setForm({ ...form, preferred_contact_channel: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {enums.contact_channels.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Assign Agent</Label>
            <Select value={form.assigned_agent_id || "__none"} onValueChange={(v) => setForm({ ...form, assigned_agent_id: v === "__none" ? "" : v })}>
              <SelectTrigger data-testid="assign-agent-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Unassigned</SelectItem>
                {agents.filter((a) => a.role === "agent").map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name} — {a.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>VAT Number</Label>
            <Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} />
          </div>
          <div>
            <Label>Company Size</Label>
            <Input value={form.company_size} onChange={(e) => setForm({ ...form, company_size: e.target.value })}
              placeholder="e.g. 11-50" />
          </div>
          <div className="col-span-2">
            <Label>Product Interest</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {enums.product_interests.map((p) => {
                const active = form.product_interest.includes(p);
                return (
                  <button key={p} type="button" onClick={() => toggleProduct(p)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      active
                        ? "bg-[#1A3A21] text-white border-[#1A3A21]"
                        : "bg-white text-gray-700 border-gray-300 hover:border-[#1A3A21]"
                    }`}>
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="save-lead-button"
            className="bg-[#1A3A21] hover:bg-[#142C19] text-white">
            {saving ? "Saving..." : "Create Lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
